import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { useDashboard } from "../context/DashboardContext";
import { db } from "../config/fbConfig";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import "../styles/Home.css";
import Loader from "./Loader";

const Dashboard = () => {
  const { user, userData } = useUserAuth();
  const { 
    dashboardData, 
    setDashboardData, 
    loading, 
    setLoading 
  } = useDashboard();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Check if data is still valid (fetched within last 5 minutes)
      const now = new Date();
      const lastFetched = dashboardData.lastFetched ? new Date(dashboardData.lastFetched) : null;
      const isDataStale = !lastFetched || (now - lastFetched) > 5 * 60 * 1000;
      
      if (!isDataStale && dashboardData.currentTerm) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch current term document
        const currentTermDoc = await getDoc(doc(db, "current-term", "current"));
        const currentTermData = currentTermDoc.data();
        const currentSessionId = currentTermData?.sessionId;
        const currentTermId = currentTermData?.termId;

        if (!currentSessionId || !currentTermId) {
          console.error("Current session or term not found");
          setLoading(false);
          return;
        }

        // Check if term changed
        const termChanged = 
          dashboardData.currentTerm?.sessionId !== currentSessionId || 
          dashboardData.currentTerm?.termId !== currentTermId;

        if (!isDataStale && !termChanged) {
          setLoading(false);
          return;
        }

        // Fetch section names
        const sectionsQuery = query(collection(db, "sections"));
        const sectionsSnapshot = await getDocs(sectionsQuery);
        const sectionsData = Object.fromEntries(
          sectionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        // Fetch session and term names
        const sessionsQuery = query(
          collection(db, "sessions"),
          where("__name__", "==", currentSessionId)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionsData = Object.fromEntries(
          sessionsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        const termsQuery = query(
          collection(db, "terms"),
          where("__name__", "==", currentTermId)
        );
        const termsSnapshot = await getDocs(termsQuery);
        const termsData = Object.fromEntries(
          termsSnapshot.docs.map((doc) => [doc.id, doc.data().name])
        );

        // Fetch all classes for current term
        const classesQuery = query(
          collection(db, "classes"),
          where("session", "==", currentSessionId),
          where("term", "==", currentTermId)
        );
        const classesSnapshot = await getDocs(classesQuery);
        const currentTermClassIds = classesSnapshot.docs.map((doc) => doc.id);
        const classMap = Object.fromEntries(
          classesSnapshot.docs.map((doc) => {
            const data = doc.data();
            return [
              doc.id,
              {
                name: data.name || "Unknown Class",
                students: data.students || [],
                section: data.section || "",
              },
            ];
          })
        );

        if (userData?.status === "Parent" && userData?.isActive === true) {
          // PARENT VIEW
          const kidIds = userData.kids || [];
          let kidsData = [];

          if (kidIds.length > 0) {
            const chunks = [];
            for (let i = 0; i < kidIds.length; i += 10) {
              chunks.push(kidIds.slice(i, i + 10));
            }

            for (const chunk of chunks) {
              const studentsQuery = query(
                collection(db, "students"),
                where("__name__", "in", chunk)
              );
              const studentsSnapshot = await getDocs(studentsQuery);
              kidsData = [
                ...kidsData,
                ...studentsSnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const kidsByClass = {};
          const kidsFinancials = [];
          let totalKids = 0;
          let totalExpected = 0;
          let totalActual = 0;

          for (const kid of kidsData) {
            const enrollments = kid.enrollments || [];
            const currentEnrollment = enrollments.find(
              (e) =>
                currentTermClassIds.includes(e.classId) &&
                classMap[e.classId]
            );

            if (currentEnrollment) {
              totalKids++;
              const classId = currentEnrollment.classId;
              const className = classMap[classId]?.name || "Unknown Class";
              const sectionId = classMap[classId]?.section || "";
              const sectionName = sectionsData[sectionId] || "Unknown Section";

              if (!kidsByClass[classId]) {
                kidsByClass[classId] = {
                  classId,
                  className,
                  sectionId,
                  sectionName,
                  kidCount: 0,
                };
              }
              kidsByClass[classId].kidCount++;

              const fees = Number(currentEnrollment.fees) || 0;
              const paid = Number(currentEnrollment.paid) || 0;
              const balance = fees - paid;

              totalExpected += fees;
              totalActual += paid;

              kidsFinancials.push({
                kidId: kid.id,
                kidName: `${kid.firstName} ${kid.lastName}`,
                classId,
                className,
                sectionName,
                expected: fees,
                actual: paid,
                balance,
              });
            }
          }

          const classesWithKids = Object.values(kidsByClass).sort((a, b) =>
            a.className.localeCompare(b.className)
          );

          setDashboardData(prev => ({
            ...prev,
            parentData: {
              classes: classesWithKids,
              kidsFinancials,
              totalKids,
              totalFinancials: {
                expected: totalExpected,
                actual: totalActual,
                balance: totalExpected - totalActual,
              },
            },
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId
            }
          }));

        } else if (userData?.status === "Teacher" && userData?.isActive === true) {
          // TEACHER VIEW
          const teachingAssignments = userData.teachingAssignments || [];
          const assignedClassIds = [...new Set(teachingAssignments.map(ta => ta.classId))];
          const currentTermAssignedClassIds = assignedClassIds.filter(id => 
            currentTermClassIds.includes(id)
          );

          const currentTermAssignments = teachingAssignments.filter(ta => 
            currentTermClassIds.includes(ta.classId)
          );
          const totalUniqueSubjects = [...new Set(currentTermAssignments.map(ta => ta.subjectId))].length;

          const classesQuery = query(
            collection(db, "classes"),
            where("__name__", "in", currentTermAssignedClassIds.length > 0 ? currentTermAssignedClassIds : ["dummy"]),
            where("session", "==", currentSessionId),
            where("term", "==", currentTermId)
          );
          const classesSnapshot = await getDocs(classesQuery);

          const classesWithStudents = await Promise.all(
            classesSnapshot.docs.map(async (classDoc) => {
              const classData = classDoc.data();
              const studentIds = classData.students || [];
              
              let maleCount = 0;
              let femaleCount = 0;
              
              if (studentIds.length > 0) {
                const chunks = [];
                for (let i = 0; i < studentIds.length; i += 10) {
                  chunks.push(studentIds.slice(i, i + 10));
                }
                
                for (const chunk of chunks) {
                  const studentsQuery = query(
                    collection(db, "students"),
                    where("__name__", "in", chunk)
                  );
                  const studentsSnapshot = await getDocs(studentsQuery);
                  studentsSnapshot.forEach((studentDoc) => {
                    const gender = studentDoc.data().gender || "";
                    if (gender.toLowerCase() === "male") maleCount++;
                    else if (gender.toLowerCase() === "female") femaleCount++;
                  });
                }
              }

              const subjectCount = [...new Set(
                currentTermAssignments
                  .filter(ta => ta.classId === classDoc.id)
                  .map(ta => ta.subjectId)
              )].length;

              return {
                classId: classDoc.id,
                className: classData.name || "Unknown Class",
                sectionId: classData.section || "",
                sectionName: sectionsData[classData.section] || "Unknown Section",
                sessionId: classData.session || "",
                sessionName: sessionsData[classData.session] || "Unknown Session",
                termId: classData.term || "",
                termName: termsData[classData.term] || "Unknown Term",
                totalStudents: studentIds.length,
                maleStudents: maleCount,
                femaleStudents: femaleCount,
                subjectCount,
              };
            })
          );

          setDashboardData(prev => ({
            ...prev,
            teacherClasses: classesWithStudents,
            totalSubjects: totalUniqueSubjects,
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId
            }
          }));

        } else if (
          (userData?.status === "Admin" && userData?.level === "Supreme") || 
          (userData?.status === "Cashier" && userData?.isActive === true)
        ) {
          // ADMIN/CASHIER VIEW
          const classCount = classesSnapshot.size;

          const sectionSet = new Set(
            classesSnapshot.docs.map((doc) => doc.data().section)
          );
          const sectionCount = sectionSet.size;

          const teachersQuery = query(
            collection(db, "users"),
            where("status", "==", "Teacher")
          );
          const teachersSnapshot = await getDocs(teachersQuery);

          const activeTeachers = teachersSnapshot.docs.filter(doc => {
            const teacherData = doc.data();
            const assignments = teacherData.teachingAssignments || [];
            return assignments.some(assignment => 
              currentTermClassIds.includes(assignment.classId)
            );
          });

          const activeTeachersWithDetails = await Promise.all(
            activeTeachers.map(async teacherDoc => {
              const teacherData = teacherDoc.data();
              const assignments = teacherData.teachingAssignments || [];
              
              const currentTermAssignments = assignments.filter(assignment => 
                currentTermClassIds.includes(assignment.classId)
              );
              
              const currentTermSubjects = [...new Set(
                currentTermAssignments.map(a => a.subjectId))
              ];
              
              return {
                id: teacherDoc.id,
                ...teacherData,
                currentTermSubjectsCount: currentTermSubjects.length,
                currentTermClassCount: [...new Set(
                  currentTermAssignments.map(a => a.classId)
                )].length
              };
            })
          );

          const allStudentIds = new Set();
          Object.values(classMap).forEach((cls) => {
            cls.students.forEach((studentId) => allStudentIds.add(studentId));
          });

          let studentsData = [];
          if (allStudentIds.size > 0) {
            const studentIdArray = Array.from(allStudentIds);
            const chunks = [];
            for (let i = 0; i < studentIdArray.length; i += 10) {
              chunks.push(studentIdArray.slice(i, i + 10));
            }

            for (const chunk of chunks) {
              const studentsQuery = query(
                collection(db, "students"),
                where("__name__", "in", chunk)
              );
              const studentsSnapshot = await getDocs(studentsQuery);
              studentsData = [
                ...studentsData,
                ...studentsSnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })),
              ];
            }
          }

          const studentGenderMap = Object.fromEntries(
            studentsData.map((student) => [student.id, student.gender || ""])
          );

          let maleCount = 0;
          let femaleCount = 0;
          const studentsPerClass = {};
          let totalExpected = 0;
          let totalActual = 0;
          const financialStatsByClass = {};

          Object.entries(classMap).forEach(([classId, cls]) => {
            studentsPerClass[classId] = { male: 0, female: 0, total: 0, sectionName: sectionsData[cls.section] || "Unknown Section" };
            financialStatsByClass[classId] = { 
              expected: 0, 
              actual: 0, 
              balance: 0,
              className: cls.name || "Unknown Class",
              sectionId: cls.section || "",
              sectionName: sectionsData[cls.section] || "Unknown Section"
            };

            cls.students.forEach((studentId) => {
              const gender = studentGenderMap[studentId] || "";
              
              if (gender.toLowerCase() === "male") {
                studentsPerClass[classId].male++;
                maleCount++;
              } else if (gender.toLowerCase() === "female") {
                studentsPerClass[classId].female++;
                femaleCount++;
              }

              studentsPerClass[classId].total = studentsPerClass[classId].male + studentsPerClass[classId].female;

              const student = studentsData.find(s => s.id === studentId);
              if (student && student.enrollments) {
                const enrollment = student.enrollments.find(e => e.classId === classId) || { fees: 0, paid: 0 };
                const fees = Number(enrollment.fees) || 0;
                const paid = Number(enrollment.paid) || 0;
                const balance = fees - paid;

                financialStatsByClass[classId].expected += fees;
                financialStatsByClass[classId].actual += paid;
                financialStatsByClass[classId].balance += balance;

                totalExpected += fees;
                totalActual += paid;
              }
            });
          });

          const studentsByClass = Object.entries(studentsPerClass)
            .map(([classId, counts]) => ({
              className: classMap[classId].name,
              section: classMap[classId].section,
              sectionName: sectionsData[classId] || "Unknown Section",
              ...counts,
            }))
            .filter((cls) => cls.total > 0)
            .sort((a, b) => a.className.localeCompare(b.className));

          const financialStatsByClassArray = Object.entries(financialStatsByClass)
            .map(([classId, stats]) => ({
              className: classMap[classId].name,
              section: classMap[classId].section,
              ...stats,
            }))
            .sort((a, b) => a.className.localeCompare(b.className));

          setDashboardData(prev => ({
            ...prev,
            adminStats: {
              teacherCount: activeTeachers.length,
              activeTeachers: activeTeachersWithDetails,
              totalSubjectsCurrentTerm: activeTeachersWithDetails.reduce(
                (sum, teacher) => sum + teacher.currentTermSubjectsCount, 0
              ),
              studentCount: {
                male: maleCount,
                female: femaleCount,
                total: maleCount + femaleCount,
              },
              classCount,
              studentsByClass,
              sectionCount,
              financialStats: {
                expected: totalExpected,
                actual: totalActual,
                balance: totalExpected - totalActual,
              },
              financialStatsByClass: financialStatsByClassArray,
            },
            lastFetched: new Date().toISOString(),
            currentTerm: {
              sessionId: currentSessionId,
              termId: currentTermId
            }
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userData) {
      fetchData();
    }
  }, [user, userData]);

  if (loading || !userData) {
    return <Loader text={"LOADING"} />;
  }

  const { 
    teacherClasses, 
    totalSubjects, 
    adminStats, 
    parentData 
  } = dashboardData;

  return (
    <div className="dashboard-container padding-bottom-d">
      {userData.status === "Parent" && userData.isActive === true ? (
        <div className="parent-dashboard">
          <p className="caps login-heading page-heading page-heading-d">MY KIDS' CLASSES</p>
          <div className="stat-content-by-class">
            <p className="bold stat-heading-d">All Kids</p>
            <i className="fa-solid fa-user-graduate size1" style={{ fontSize: '2rem', color: '#E56B6F' }}></i>
            <p className="male-female-stat">
              <span className="bold italics">{parentData.totalKids}</span> Kid{parentData.totalKids !== 1 ? 's' : ''}
            </p>
          </div>
          {parentData.classes.length > 0 ? (
            <div className="stats-grid">
              {parentData.classes.map((cls, index) => (
                <div key={index} className="stats-grid-by-class">
                  <div className="stat-content-by-class stat-content-by-class1">
                    <p className="bold stat-heading-d">{cls.className}</p>
                    <p className="size55 italics">({cls.sectionName})</p>
                    <i className="fa-solid fa-user-graduate size1" style={{ fontSize: '1.5rem', color: '#E56B6F' }}></i>
                    <p className="male-female-stat">
                      <span className="bold italics">{cls.kidCount}</span> Kid{cls.kidCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-subjects">No kids enrolled this term</p>
          )}

          <p className="caps login-heading page-heading page-heading-d">FINANCIAL DETAILS</p>
          <div className="stat-content-by-class">
            <p className="bold stat-heading-d">All Kids</p>
            <i className="fa-solid fa-money-bill-wave" style={{ fontSize: '2rem', color: '#2ECC71' }}></i>
            <p className="male-female-stat">
              Expected: <span className="bold">₦ {parentData.totalFinancials.expected.toLocaleString()}</span>
            </p>
            <p className="male-female-stat">
              Actual: <span className="bold">₦ {parentData.totalFinancials.actual.toLocaleString()}</span>
            </p>
            <p className="male-female-stat">
              Balance: <span className="bold">₦ {parentData.totalFinancials.balance.toLocaleString()}</span>
            </p>
          </div>
          {parentData.kidsFinancials.length > 0 ? (
            <div className="stats-grid">
              {parentData.kidsFinancials.map((kid, index) => (
                <div key={index} className="stats-grid-by-class">
                  <div className="stat-content-by-class stat-content-by-class1">
                    <p className="bold stat-heading-d">{kid.kidName}</p>
                    <p className="size55 italics">{kid.className} ({kid.sectionName})</p>
                    <i className="fa-solid fa-coins" style={{ fontSize: '1.5rem', color: '#F1C40F' }}></i>
                    <p className="size5 male-female-stat">
                      Expected: <span className="bold">₦ {kid.expected.toLocaleString()}</span>
                    </p>
                    <p className="size5 male-female-stat">
                      Actual: <span className="bold">₦ {kid.actual.toLocaleString()}</span>
                    </p>
                    <p className="size5 male-female-stat">
                      Balance: <span className="bold">₦ {kid.balance.toLocaleString()}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-subjects">No financial details available</p>
          )}
        </div>
      ) : userData.status === "Admin" || userData.status === "Cashier" ? (
        <div className="admin-dashboard">
          <p className="caps login-heading page-heading page-heading-d">SCHOOL OVERVIEW</p>
          <div className="stats-grid">
            <div
              className="stat-content"
              onClick={() => navigate("/sections")}
            >
              <i className="fa-solid fa-school" style={{fontSize: '3rem', color: '#9B59B6'}}></i>
              <p className="stat-value"><span className="bold italics size2">{adminStats.sectionCount}</span><br />Sections</p>
            </div>
            <div
              className="stat-content"
              onClick={() => navigate("/classes")}
            >
              <i className="fa-solid fa-chalkboard-user size1" style={{fontSize: '3rem', color: '#5C9E7D'}}></i>
              <p className="stat-value"><span className="bold italics size2">{adminStats.classCount}</span><br />Classes</p>              
            </div>
            <div
              className="stat-content"
              onClick={() => navigate("/teachers")}
            >
              <i className="fa-solid fa-person-chalkboard size1" style={{fontSize: '3rem', color: '#3A7CA5'}}></i>
              <p className="stat-value"><span className="bold italics size2">{adminStats.teacherCount}</span><br />Active Teachers</p>
            </div>

            <div
              className="stat-content"
              onClick={() => navigate("/students")}
            >
              <i className="fa-solid fa-user-graduate size1" style={{fontSize: '3rem', color: '#E56B6F'}}></i>
              <p className="stat-value"><span className="bold italics size2">{adminStats.studentCount.total}</span><br />Students</p>                
            </div>                        
          </div>
        <p className="caps login-heading page-heading page-heading-d">STUDENTS BY CLASS</p>

        <div className="stat-content-by-class">
          <p className="bold stat-heading-d">All Classes</p>
          <p className="male-female-stat"><span className="bold italics">{adminStats.studentCount.total}</span>&nbsp;Students</p>
          <div className="gender-stats">
            <span className="male-female-stat"><span className="bold italics">{adminStats.studentCount.male}</span> Males</span>
            <i className="fa-solid fa-children" style={{fontSize: '3rem', color: '#D8A7B1', transform: 'scaleX(-1)' }}></i>
            <span className="male-female-stat"><span className="bold italics">{adminStats.studentCount.female}</span> Females</span>
          </div>
        </div>

        {adminStats.studentsByClass.length > 0 && (
          <div className="section-grouping">
            {/* Group classes by section */}
            {Object.entries(
              adminStats.studentsByClass.reduce((acc, cls) => {
                if (!acc[cls.sectionName]) {
                  acc[cls.sectionName] = [];
                }
                acc[cls.sectionName].push(cls);
                return acc;
              }, {})
            ).map(([sectionName, classes]) => (
              <div key={sectionName} className="section-container">
                <h3 className="page-heading page-heading-d">{sectionName} Section</h3>
                <div className="stats-grid">
                  {classes.map((cls, index) => (
                    <div key={index} className="stats-grid-by-class">
                      <div className="stat-content-by-class stat-content-by-class1">
                        <p className="bold stat-heading-d">{cls.className}</p>
                        <p><span className="bold italics male-female-stat">{cls.total}</span> &nbsp;Students</p>
                        <div className="gender-stats1">
                          <span className="male-female-stat">{cls.male}<span className="size6"> Males</span></span>
                          <i className="fa-solid fa-children" style={{fontSize: '2rem', color: '#E8A87C', transform: 'scaleX(-1)' }}></i>
                          <span className="male-female-stat">{cls.female}<span className="size6"> Females</span></span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {(userData.status === "Cashier" || userData.level === "Supreme") && (
          <>
            <p className="caps login-heading page-heading page-heading-d">FINANCIAL OVERVIEW</p>

            <div className="stat-content-by-class">
              <p className="bold stat-heading-d">All Classes</p>
              <i className="fa-solid fa-money-bill-wave" style={{fontSize: '2rem', color: '#2ECC71'}}></i>
              <p className="male-female-stat">Expected: <span className="bold">&nbsp;₦ {adminStats.financialStats.expected.toLocaleString()}</span></p>
              <p className="male-female-stat">Actual: <span className="bold">&nbsp;₦ {adminStats.financialStats.actual.toLocaleString()}</span></p>
              <p className="male-female-stat">Balance: <span className="bold">&nbsp;₦ {adminStats.financialStats.balance.toLocaleString()}</span></p>
            </div>

            {adminStats.financialStatsByClass.length > 0 && (
              <div className="section-grouping">
                {/* Group financial stats by section */}
                {Object.entries(
                  adminStats.financialStatsByClass.reduce((acc, cls) => {
                    if (!acc[cls.sectionName]) {
                      acc[cls.sectionName] = [];
                    }
                    acc[cls.sectionName].push(cls);
                    return acc;
                  }, {})
                ).map(([sectionName, classes]) => (
                  <div key={sectionName} className="section-container">
                    <h3 className="page-heading page-heading-d">{sectionName} Section</h3>
                    <div className="stats-grid">
                      {classes.map((cls, index) => (
                        <div key={index} className="stats-grid-by-class">
                          <div className="stat-content-by-class stat-content-by-class1">
                            <p className="bold stat-heading-d">{cls.className}</p>
                            <i className="fa-solid fa-coins" style={{fontSize: '1.5rem', color: '#F1C40F'}}></i>
                            <p className="size5 male-female-stat">Expected: <span className="bold">&nbsp;₦ {cls.expected.toLocaleString()}</span></p>
                            <p className="size5 male-female-stat">Actual: <span className="bold">&nbsp;₦ {cls.actual.toLocaleString()}</span></p>
                            <p className="size5 male-female-stat">Balance: <span className="bold">&nbsp;₦ {cls.balance.toLocaleString()}</span></p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      ) : userData.status === "Teacher" ? (
        <div className="teacher-dashboard">
          <p className="caps login-heading page-heading page-heading-d">MY CLASSES</p>
          
          {teacherClasses.length > 0 ? (
            <>
              <div className="stat-content-by-class">                
                <p className="bold">All Classes</p>
                <i className="fa-solid fa-chalkboard-user size1" style={{fontSize: '2rem', color: '#5C9E7D'}}></i>
                <p><span className="bold italics">{totalSubjects}</span> Subjects</p>
              </div>
              <div className="stats-grid">
                {teacherClasses.map((cls, index) => (
                  <div key={index} className="stats-grid-by-class">
                    <div className="stat-content-by-class stat-content-by-class1">
                      <p className="bold">{cls.className}</p>
                      <i className="fa-solid fa-person-chalkboard size1" style={{fontSize: '1.5rem', color: '#3A7CA5'}}></i>
                      <p><span className="bold italics">{cls.subjectCount}</span> Subject(s)</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="caps login-heading page-heading page-heading-d">MY STUDENTS</p>
              <div className="stats-grid">
                {teacherClasses.map((cls, index) => (
                  <div key={index} className="stats-grid-by-class">
                    <div className="stat-content-by-class stat-content-by-class1">
                      <p className="bold">{cls.className}</p>
                      <p><span className="bold italics">{cls.totalStudents}</span> Students</p>
                      <div className="gender-stats1">
                        <span className="male-stat">{cls.maleStudents} <span className="size6">Males</span></span>
                        <i className="fa-solid fa-children" style={{fontSize: '2rem', color: '#E8A87C', transform: 'scaleX(-1)' }}></i>
                        <span className="female-stat">{cls.femaleStudents} <span className="size6">Females</span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="no-subjects">No classes assigned this term</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Dashboard;
